<template>
    <div class="button-group">
        <!-- Edit -->
        <md-button
            v-if="!blockoutDay.inEditMode && !blockoutDay.inAddMode && !isEndDateAlreadyPassed(blockoutDay)"
            :disabled="disableActionButtons"
            :class="disableActionButtons ? 'md-disabled' : ''"
            title="Edit blockout day"
            class="md-warning md-just-icon md-round"
            @click="editBlockoutDay(blockoutDay)"
        >
            <md-icon>edit</md-icon>
        </md-button>

        <!-- Save -->
        <md-button
            v-if="blockoutDay.inEditMode || blockoutDay.inAddMode"
            title="Save"
            class="md-success md-just-icon md-round"
            @click="handleSaveChanges(blockoutDay)"
        >
            <md-icon>check</md-icon>
        </md-button>

        <!-- Upload Attachments -->
        <blockout-days-add-attachment-button
            :blockout-day="blockoutDay"
            :disable-action-buttons="disableActionButtons"
            @saveBlockoutDay="handleSaveBlockoutDay"
            @imageListUpdate="handleImageListUpdate"
            @documentListUpdate="handleDocumentListUpdate"
        />

        <!-- Delete -->
        <blockout-days-delete-button
            :blockout-day="blockoutDay"
            :disable-action-buttons="disableActionButtons"
            :is-end-date-already-passed="isEndDateAlreadyPassed"
            @delete="handleDeleteBlockoutDay"
        />

        <!-- Remove -->
        <md-button
            v-if="blockoutDay.inAddMode"
            title="Remove"
            class="md-danger md-just-icon md-round"
            @click="removeBlockoutDay()"
        >
            <md-icon>remove</md-icon>
        </md-button>

        <!-- Undo -->
        <md-button
            v-if="blockoutDay.inEditMode"
            title="Undo changes"
            class="md-info md-just-icon md-round"
            @click="undoChangesOnBlockoutDay(blockoutDay)"
        >
            <md-icon>restart_alt</md-icon>
        </md-button>
    </div>
</template>

<script>
import moment from 'moment';
import { handleRequests } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { BlockBlobClient } from '@azure/storage-blob';
import BlockoutDaysDeleteButton from './BlockoutDaysDeleteButton';
import BlockoutDaysAddAttachmentButton from './BlockoutDaysAddAttachmentButton';

export default {
    name: 'BlockoutDaysButtonGroup',
    mixins: [GeneralMixin],
    components: {
        BlockoutDaysDeleteButton,
        BlockoutDaysAddAttachmentButton
    },
    props: {
        blockoutDay: {
            type: Object,
            default: () => {},
            required: true
        },
        index: {
            type: Number,
            required: true
        },
        teamMemberRosterConfigurationId: {
            type: Number,
            required: true
        },
        disableActionButtons: {
            type: Boolean,
            default: false
        },
        imageList: {
            type: Array,
            default: () => []
        },
        documentList: {
            type: Array,
            default: () => []
        },
        showErrorMessage: {
            type: Function,
            default: () => {}
        },
        isNotValidDate: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            images: [],
            documents: []
        };
    },
    watch: {
        imageList(newVal, oldVal) {
            this.images = newVal;
        },
        documentList(newVal, oldVal) {
            this.documents = newVal;
        }
    },
    methods: {
        isEndDateAlreadyPassed(blockoutDay) {
            if (!blockoutDay?.endDate || !blockoutDay.teamMemberRosterBlockoutDayId)
                return false;

            return moment(blockoutDay.endDate).isBefore(moment(), 'day');
        },
        editBlockoutDay(blockoutDay) {
            this.$emit('update', blockoutDay);
        },
        undoChangesOnBlockoutDay(blockoutDay) {
            this.$emit('undo-changes', blockoutDay);
        },
        removeBlockoutDay() {
            this.$emit('remove', this.index);
        },
        async handleSaveChanges(blockoutDay) {
            if (!blockoutDay)
                return;

            if (this.isNotValidDate(blockoutDay.startDate, blockoutDay.endDate)) {
                blockoutDay.isTouched = true;
                return;
            }

            this.$_handleLoaderState(true, 'SAVING...');
            await this.saveBlockoutDay(blockoutDay, blockoutDay.inAddMode);

            this.$emit('saveChanges', blockoutDay);
        },
        handleDeleteBlockoutDay(event) {
            this.$emit('delete', event);
        },
        handleSaveBlockoutDay({ blockoutDay, addMode }) {
            if (!blockoutDay.inAddMode && !blockoutDay.inEditMode) {
                this.$_handleLoaderState(true, 'SAVING...');
                setTimeout(() => this.saveBlockoutDay(blockoutDay, addMode), 300);
            }
        },
        async saveBlockoutDay(blockoutDay, addMode) {
            const response = await this.processSaveBlockoutDay(blockoutDay, addMode);
            if (response?.data && (response.data.photoUploadUrls || response.data.documentUploadUrls)) {
                const uploadPromiseList = [];
                const uploadDocumentPromiseList = [];

                if (response.data.photoUploadUrls) {
                    for (let i = 0; i < response.data.photoUploadUrls.length; i++) {
                        uploadPromiseList.push(
                            this.onUploadImage(response.data.photoUploadUrls[i], this.images[i].file)
                        );
                    }
                }

                if (response.data.documentUploadUrls) {
                    for (let i = 0; i < response.data.documentUploadUrls.length; i++) {
                        uploadDocumentPromiseList.push(
                            this.onUploadDocument(response.data.documentUploadUrls[i], this.documents[i].file)
                        );
                    }
                }

                Promise.all(uploadDocumentPromiseList.concat(uploadPromiseList)).then(() => {
                    this.handleBlockoutDayUpdated();

                    blockoutDay.inAddMode = false;
                    blockoutDay.inEditMode = false;

                    this.handleImageListUpdate([]);
                    this.handleDocumentListUpdate([]);
                });
            } else if (!response) {
                // Error has occurred
                this.undoChangesOnBlockoutDay(blockoutDay);

                this.handleImageListUpdate([]);
                this.handleDocumentListUpdate([]);

                this.$_handleLoaderState(false);
            } else {
                this.handleBlockoutDayUpdated();

                blockoutDay.inAddMode = false;
                blockoutDay.inEditMode = false;

                this.handleImageListUpdate([]);
                this.handleDocumentListUpdate([]);
            }
        },
        async processSaveBlockoutDay(blockoutDay, addMode) {
            const photoFiles = this.images.map((item) => {
                return item.file.name;
            });

            const documentFiles = this.documents.map((item) => {
                return item.file.name;
            });

            let attachment = null;
            if (photoFiles.length > 0 || documentFiles.length > 0) {
                attachment = {
                    photoFileNames: photoFiles,
                    documentFileNames: documentFiles
                };
            }

            let payload = {
                method: 'post',
                data: {
                    ...blockoutDay,
                    teamMemberRosterConfigurationId: this.teamMemberRosterConfigurationId,
                    attachment
                }
            };

            let url = '/api/team-member-roster-blockoutday';

            if (!addMode) {
                url = `/api/team-member-roster-blockoutday/${blockoutDay.teamMemberRosterBlockoutDayId}`;

                payload = {
                    method: 'put',
                    data: {
                        ...blockoutDay,
                        attachment
                    }
                };
            }

            try {
                const response = await handleRequests(url, payload);
                this.$notify({
                    message: 'Successfully saved unavailable day',
                    type: 'success'
                });

                return response;
            } catch (e) {
                const message = 'Could not save unavailable day';
                this.showErrorMessage(this, message, e);
                this.$_handleLoaderState(false);
                return null;
            }
        },
        async onUploadImage(notePhotoUrl, photofile) {
            const blockBlobClient = new BlockBlobClient(notePhotoUrl);

            try {
                await blockBlobClient.upload(photofile, photofile.size, {
                    blobHTTPHeaders: {
                        blobContentType: photofile.type
                    }
                });
            } catch (error) {
                this.$notify({
                    message: `Error in uploading photo (${error})`,
                    type: 'danger'
                });
            }
        },
        async onUploadDocument(noteDocumentUrl, documentfile) {
            const blockBlobClient = new BlockBlobClient(noteDocumentUrl);

            try {
                await blockBlobClient.upload(documentfile, documentfile.size, {
                    blobHTTPHeaders: {
                        blobContentType: documentfile.type
                    }
                });
            } catch (error) {
                this.$notify({
                    message: `Error in uploading document (${error})`,
                    type: 'danger'
                });
            }
        },
        handleImageListUpdate(imageList) {
            this.$emit('imageListUpdate', imageList);
        },
        handleDocumentListUpdate(documentList) {
            this.$emit('documentListUpdate', documentList);
        },
        handleBlockoutDayUpdated() {
            this.$emit('blockoutDayUpdated');
        }
    }
};
</script>

<style lang="scss" scoped>
.button-group {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
}
</style>
